import type { ComponentProps } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import { IcmPeriodPicker } from "@/icm/common/icm-period-picker";
import { ARTIFACT_CONTROLS } from "@/payroll/editor/constants";
import { proposalError } from "@/payroll/editor/state";
import type { ComparisonState } from "@/payroll/editor/types";
import { LENS, LENS_LABEL, type Lens } from "./lens";

const CONTROL = "h-7 rounded-md px-2.5 text-sm font-medium";

/**
 * The period to compare over, the run control, and which reading of the run to
 * show. Everything that decides WHAT is on screen, before the screen itself.
 */
export const ComparisonToolbar = ({
  data,
  state,
  run,
  periodStart,
  setPeriodStart,
  pickerPeriods,
  payMayMove,
  editable,
  lens,
  setLens,
}: {
  data: ComparisonState | undefined;
  state: { isPending: boolean; isFetching: boolean };
  run: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    mutate: () => void;
  };
  periodStart: string | undefined;
  setPeriodStart: (value: string) => void;
  pickerPeriods: ComponentProps<typeof IcmPeriodPicker>["periods"];
  payMayMove: boolean;
  editable: boolean;
  lens: Lens;
  setLens: (lens: Lens) => void;
}) => (
  // `border-b-0`: on Pay this row is the top of a three-band grey block, not
  // the edge of it. The block's wrapper draws the one strong line at its foot.
  <div
    className={cn(
      ARTIFACT_CONTROLS,
      "flex flex-wrap items-center gap-1.5 border-b-0",
    )}
  >
    <IcmPeriodPicker
      className="h-7 w-52 rounded-md px-2.5 shadow-none"
      periods={pickerPeriods}
      calendarPeriods={pickerPeriods}
      selectedPeriodStart={periodStart ?? data?.period.start_date}
      isLoading={state.isFetching}
      disabled={data?.running}
      onSelect={setPeriodStart}
    />
    {/* A closed proposal has nothing left to compare against: its draft is
          no longer the one a run would use, and the rest of the workspace is
          already read-only. Offering a five-minute run here is an invitation
          to waste it. */}
    {!!editable && (
      <Button
        size="xs"
        className={CONTROL}
        loading={run.isPending || data?.running}
        disabled={run.isPending || data?.running || !payMayMove}
        title={
          payMayMove ? undefined : "There is no change that could move pay"
        }
        onClick={() => run.mutate()}
      >
        {data?.ready ? "Run again" : "Run comparison"}
      </Button>
    )}
    {!!data?.running && (
      <p className="text-sm text-secondary-text" role="status">
        Running both versions over this period. This takes a few minutes.
      </p>
    )}
    {!!run.isError && (
      <p className="text-sm" role="alert">
        {proposalError(run.error)}
      </p>
    )}
    {!!data?.error && !data.running && (
      <p className="text-sm" role="alert">
        {data.error}
      </p>
    )}
    {/* Last, and pushed to the far edge.

        It sat third, between Run again and the run's own status line, which put
        a view switch in the middle of the controls that produce the thing being
        viewed. The period and the run button are inputs: they decide what gets
        calculated. This decides how you look at the answer, so it reads as the
        other end of the row, and the status line stays next to the button that
        causes it. */}
    <Stack
      gap="none"
      align="stretch"
      direction="row"
      role="group"
      aria-label="Comparison view"
      className="ml-auto flex items-center gap-0.5 rounded-md bg-surface-secondary p-0.5"
    >
      {Object.values(LENS).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={lens === option}
          onClick={() => setLens(option)}
          className={cn("rounded px-2 py-1 text-sm font-medium", {
            "bg-surface-primary text-primary-text": lens === option,
            "text-secondary-text hover:text-primary-text": !(lens === option),
          })}
        >
          {LENS_LABEL[option]}
        </button>
      ))}
    </Stack>
  </div>
);
